/**
 * System notifications of the web shell (I-2915): a notification whose
 * setting for this browser is „Glocke + Systemhinweis" (`system`,
 * `notificationDeviceSettings.ts`) also pops up as a notification of the
 * browser (the Notification API) — in whatever app of the shell is open.
 * Web mail and web calendar have none of their own; this is the one place.
 *
 * - **What pops up:** a `notification.new` push — master's „visible now",
 *   also a reminder whose time has come — that is unread, of the session's
 *   company or of none, and `system` here
 *   (`browserSettingOf`, with master's type register for the types that are
 *   off by default). Each once per tab; the tag lets the browser keep one
 *   banner per notification when several tabs are open.
 * - **The permission is asked on a gesture, never on load:** when the user
 *   picks „Glocke + Systemhinweis" for the browser in the settings list
 *   (`askForPermissionOnPick`), or from a kit toast the shell offers once per
 *   page when a notification should pop up but the browser was never asked.
 * - **A click** brings the tab forward, opens the notification's link the way
 *   the bell does (`notificationLinks.ts`) and marks it read once it opened.
 */
import { onBeforeUnmount, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useNxSocket } from '@blpde/connect';
import { useNxToasts } from '@blpde/nx-ui';
import { openNotificationLink } from '@/notificationLinks';
import { hasAuthCredentials } from '@/auth';
import {
  browserSettingOf,
  isForeignCompany,
  markNotificationRead,
  notificationTypeRegister,
  shellSettingsKnown,
  type MasterNotification,
  type NotificationType,
} from '@/notifications';
import { useNotificationText } from '@/notificationText';
import { sessionCompanyID } from '@/scope';
import {
  notificationApi,
  requestSystemNotificationPermission,
  systemNotificationPermission,
} from '@/systemNotificationPermission';

/** What the announcer needs of the shell. */
export interface SystemNotificationDeps {
  /** Resolves once the shell's settings are known (`shellSettingsKnown`). */
  settings(): Promise<void>;
  /** master's type register by key (types that are off by default). */
  types(): Promise<ReadonlyMap<string, NotificationType>>;
  /** The company the session works in; 0: none, null: not known. */
  company(): Promise<number | null>;
  /** The banner's title and body, in the user's language. */
  text(n: MasterNotification): { title: string; body: string };
  /** Opens the notification's link; true when it opened. */
  open(n: MasterNotification): Promise<boolean>;
  markRead(id: number): Promise<void>;
  /**
   * Offers to ask the browser's permission (the kit toast): `ask` runs on
   * the user's click. Called at most once per announcer.
   */
  offerPermission(ask: () => void): void;
}

/** The shell's tag of a notification's banner: one banner per notification across tabs. */
export function systemNotificationTag(n: Pick<MasterNotification, 'id'>): string {
  return `formica-notification-${n.id}`;
}

/**
 * The announcer of one tab: `announce` takes a `notification.new` row and
 * shows it as a system notification when it is one here.
 */
export function createSystemNotifications(deps: SystemNotificationDeps) {
  /** What this tab announced: id → revision. */
  const announced = new Map<number, number>();
  let offered = false;

  function show(n: MasterNotification): Notification | null {
    const api = notificationApi();
    if (!api || api.permission !== 'granted') return null;
    const { title, body } = deps.text(n);
    let banner: Notification;
    try {
      banner = new api(title, { body, tag: systemNotificationTag(n) });
    } catch {
      return null;
    }
    banner.onclick = (event) => {
      event.preventDefault?.();
      try {
        globalThis.focus?.();
      } catch {
        // A kiosk may refuse the focus; the link still opens.
      }
      banner.close();
      void deps.open(n).then((opened) => {
        if (opened && !n.readAt) return deps.markRead(n.id);
        return undefined;
      });
    };
    return banner;
  }

  /** Whether the row pops up here: unread, of this company, `system` in this browser. */
  async function popsUp(n: MasterNotification): Promise<boolean> {
    if (!n?.id || n.readAt || n.deletedAt) return false;
    // The setting decides below: right after a sign-in it may not have been
    // read yet, and the defaults of a browser are not the user's settings.
    await deps.settings().catch(() => undefined);
    if (n.companyID) {
      // A row of a company pops up only where the session works in it.
      const company = await deps.company().catch(() => null);
      if (company === null || isForeignCompany(n, company)) return false;
    }
    let register: ReadonlyMap<string, NotificationType> | null = null;
    try {
      register = await deps.types();
    } catch {
      // Without the register every type counts as on by default.
    }
    return browserSettingOf(n, register) === 'system';
  }

  async function announce(n: MasterNotification): Promise<Notification | null> {
    if (!(await popsUp(n))) return null;
    const revision = Number(n.changeRevision ?? 0);
    if (announced.get(n.id) === revision) return null;
    announced.set(n.id, revision);
    if (announced.size > 500) announced.delete(announced.keys().next().value!);
    const permission = systemNotificationPermission();
    if (permission === 'default' && !offered) {
      offered = true;
      deps.offerPermission(() => {
        void requestSystemNotificationPermission().then((granted) => {
          if (granted === 'granted') show(n);
        });
      });
      return null;
    }
    return show(n);
  }

  return { announce };
}

/**
 * The shell's system notifications: listens to master's push lane for new
 * notifications and announces each. Mount it once, in a component that
 * lives as long as the shell (ShellLayout) — not in the navbar, which a
 * route without shell chrome unmounts while reminders keep coming.
 */
export function useSystemNotifications(): void {
  const { t } = useI18n();
  const router = useRouter();
  const socket = useNxSocket();
  const { showToast } = useNxToasts();
  const { titleOf, bodyOf } = useNotificationText();
  const announcer = createSystemNotifications({
    settings: shellSettingsKnown,
    types: notificationTypeRegister,
    company: () => sessionCompanyID(),
    text: (n) => ({ title: titleOf(n), body: bodyOf(n) }),
    open: (n) => openNotificationLink(router, n.link, { companyID: n.companyID }),
    markRead: (id) => markNotificationRead(id).catch(() => undefined),
    offerPermission: (ask) =>
      showToast({
        message: t('connect.notifications.systemPermission.message'),
        kind: 'info',
        timeout: 15000,
        action: { label: t('connect.notifications.systemPermission.allow'), onClick: ask },
      }),
  });

  let unsubscribe: (() => void) | null = null;
  // Read the register once signed in: the bell's store tells a type that is
  // off by default by it when a push arrives (`browserSettingOf`). The layout
  // is mounted on the sign-in page too, where nothing may be asked.
  function warmRegister(): void {
    if (hasAuthCredentials()) void notificationTypeRegister().catch(() => undefined);
  }
  onMounted(() => {
    const channel = socket?.channel('master');
    if (!channel) return;
    warmRegister();
    window.addEventListener('formica:auth-changed', warmRegister);
    unsubscribe = channel.on('event', (env) => {
      if (env?.evt !== 'notification.new' || !env.msg || typeof env.msg !== 'object') return;
      const row = env.msg as MasterNotification;
      void announcer.announce({ ...row, id: Number(row.id ?? 0) });
    });
  });
  onBeforeUnmount(() => {
    window.removeEventListener('formica:auth-changed', warmRegister);
    unsubscribe?.();
    unsubscribe = null;
  });
}
